import React, { useState, useEffect } from "react";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import { AuthProvider } from "./context/AuthContext";
import ProductListing from "./pages/ProductListing";
import Checkout       from "./pages/Checkout";
import OrderSuccess   from "./pages/OrderSuccess";
import MyProfile           from "./pages/MyProfile";
import TermsAndConditions  from "./pages/TermsAndConditions";
import Dashboard           from "./admin/Dashboard";
import DeliveryPage        from "./pages/DeliveryPage";
import ScanPage            from "./pages/ScanPage";
import Events              from "./pages/Events";
import EventDetail         from "./pages/EventDetail";
import TicketCheckout      from "./pages/TicketCheckout";
import TicketSuccess       from "./pages/TicketSuccess";
import CartDrawer     from "./components/CartDrawer";
import CartFlyDot     from "./components/CartFlyDot";
import OwnerViewBar   from "./components/OwnerViewBar";
import { incrementReferralClick } from "./firebase/firestoreService";
import "./App.css";

export default function App() {
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const ref = params.get("ref");
    if (ref) {
      const code = ref.toUpperCase();
      localStorage.setItem("gvs_ref", code);
      // Count click once per browser session to avoid refresh double-counting
      const sessionKey = `gvs_clicked_${code}`;
      if (!sessionStorage.getItem(sessionKey)) {
        sessionStorage.setItem(sessionKey, "1");
        incrementReferralClick(code);
      }
    }
  }, []);

  const [flyDots, setFlyDots] = useState([]);

  useEffect(() => {
    const handler = (e) => {
      const cartBtn = document.querySelector(".navbar__cart-btn");
      if (!cartBtn) return;
      const to = cartBtn.getBoundingClientRect();
      setFlyDots((prev) => [
        ...prev,
        {
          id:    Date.now() + Math.random(),
          fromX: e.detail.x,
          fromY: e.detail.y,
          toX:   to.left + to.width  / 2,
          toY:   to.top  + to.height / 2,
        },
      ]);
    };
    window.addEventListener("gvs:add-to-cart", handler);
    return () => window.removeEventListener("gvs:add-to-cart", handler);
  }, []);

  const removeDot = (id) => setFlyDots((prev) => prev.filter((d) => d.id !== id));

  const [cart,     setCart]     = useState(() => {
    try { return JSON.parse(localStorage.getItem("gvs_cart")) || []; }
    catch { return []; }
  });
  const [cartOpen, setCartOpen] = useState(false);

  useEffect(() => {
    localStorage.setItem("gvs_cart", JSON.stringify(cart));
  }, [cart]);

  const addToCart = (item) => setCart((prev) => {
    const idx = prev.findIndex(
      (c) => c.id === item.id && c.selColor === item.selColor && c.selSize === item.selSize
    );
    if (idx !== -1) {
      const updated = [...prev];
      updated[idx] = { ...updated[idx], qty: (updated[idx].qty || 1) + 1 };
      return updated;
    }
    return [...prev, { ...item, qty: 1 }];
  });

  const removeFromCart = (i) => setCart((prev) => {
    const item = prev[i];
    if ((item.qty || 1) > 1) {
      const updated = [...prev];
      updated[i] = { ...item, qty: item.qty - 1 };
      return updated;
    }
    return prev.filter((_, idx) => idx !== i);
  });

  const clearCart  = () => setCart([]);
  const totalQty   = cart.reduce((s, i) => s + (i.qty || 1), 0);

  return (
    <AuthProvider>
    <Router>
      {flyDots.map((d) => (
        <CartFlyDot key={d.id} {...d} onDone={() => removeDot(d.id)} />
      ))}
      {cartOpen && (
        <CartDrawer
          items={cart}
          onClose={() => setCartOpen(false)}
          onRemove={removeFromCart}
        />
      )}
      <OwnerViewBar />
      <Routes>
        <Route path="/" element={
          <ProductListing
            setCartOpen={setCartOpen}
            addToCart={addToCart}
            cartCount={totalQty}
          />
        } />
        <Route path="/checkout" element={
          <Checkout
            cartItems={cart}
            cartCount={totalQty}
            onCartClick={() => setCartOpen(true)}
            clearCart={clearCart}
          />
        } />
        <Route path="/order-success" element={
          <OrderSuccess
            cartCount={0}
            onCartClick={() => setCartOpen(true)}
          />
        } />
        <Route path="/profile" element={
          <MyProfile
            cartCount={totalQty}
            onCartClick={() => setCartOpen(true)}
          />
        } />
        <Route path="/terms" element={<TermsAndConditions />} />
        <Route path="/events" element={
          <Events cartCount={totalQty} onCartClick={() => setCartOpen(true)} />
        } />
        <Route path="/events/:eventId" element={
          <EventDetail cartCount={totalQty} onCartClick={() => setCartOpen(true)} />
        } />
        <Route path="/events/:eventId/checkout" element={
          <TicketCheckout cartCount={totalQty} onCartClick={() => setCartOpen(true)} />
        } />
        <Route path="/tickets/success" element={
          <TicketSuccess cartCount={totalQty} onCartClick={() => setCartOpen(true)} />
        } />
        <Route path="/admin" element={<Dashboard />} />
        <Route path="/delivery" element={
          <DeliveryPage
            cartCount={totalQty}
            onCartClick={() => setCartOpen(true)}
          />
        } />
        <Route path="/scan" element={
          <ScanPage
            cartCount={totalQty}
            onCartClick={() => setCartOpen(true)}
          />
        } />
      </Routes>
    </Router>
    </AuthProvider>
  );
}
